import { useState, useRef, useEffect } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';

interface InlineEditProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
}

export function InlineEdit({ value, onSave, placeholder = 'Click to edit', className = '', multiline = false }: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const save = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      setDraft(value);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) save();
    if (e.key === 'Escape') cancel();
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={`group flex items-center gap-1.5 text-left hover:bg-surface-700/50 rounded-md px-1.5 py-0.5 -mx-1.5 transition-colors ${className}`}
      >
        <span className={value ? 'text-surface-200' : 'text-surface-500 italic'}>{value || placeholder}</span>
        <Pencil className="h-3 w-3 text-surface-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  const inputClasses = "w-full bg-surface-700 border border-surface-600 rounded-md px-2 py-1 text-sm text-surface-200 focus:outline-none focus:ring-1 focus:ring-nexus-500";

  return (
    <div className="flex items-start gap-1.5">
      {multiline ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`${inputClasses} min-h-[60px] resize-y`}
          rows={3}
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
        />
      )}
      {saving ? (
        <Loader2 className="h-4 w-4 text-surface-400 animate-spin mt-1.5" />
      ) : (
        <div className="flex items-center gap-0.5 mt-0.5">
          <button onClick={save} className="p-1 text-emerald-400 hover:bg-emerald-500/10 rounded"><Check className="h-3.5 w-3.5" /></button>
          <button onClick={cancel} className="p-1 text-surface-400 hover:bg-surface-700 rounded"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}
